import React, { useState, CSSProperties } from "react";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

// ─── Types ────────────────────────────────────────────────────────────
interface Video {
  id: number;
  title: string;
  duration: string;
  description: string;
  url: string;
}

interface Category {
  category: string;
  icon: string;
  videos: Video[];
}

// ─── Video Data ───────────────────────────────────────────────────────
const documentationData: Category[] = [
  {
    category: "Products",
    icon: "🥦",
    videos: [
      {
        id: 1,
        title: "Category and Product",
        duration: "",
        description: "Learn how to create and manage product categories and products with SKU and pricing in VegPOS.",
        url: "/videos/Category and Product.mp4",
      },
    
    ],
  },
  {
    category: "Delivery",
    icon: "🚚",
    videos: [
      {
        id: 3,
        title: "Routes and Restaurant",
        duration: "",
        description: "How to set up delivery routes and link them to restaurants.",
        url: "/videos/Routes and Restuarant.mp4",
      },
      {
        id: 10,
        title: "Restaurant Portal",
        duration: "",
        description: "Guide for restaurant users on how to use the restaurant portal.",
        url: "/videos/Restuarant Portal.mp4",
      },
      {
        id: 4,
        title: "Orders and Invoices",
        duration: "",
        description: "View, process restaurant orders and generate invoices.",
        url: "/videos/Orders and Invoices.mp4",
      },
      {
        id: 5,
        title: "Delivery Planner and Demand Forecast",
        duration: "",
        description: "Plan and assign deliveries efficiently using the delivery planner and forecast future demand based on order history and trends.",
        url: "/videos/DeliveryPlanner and DemandForecast.mp4",
      },
      
    ],
  },
  {
    category: "Procurement",
    icon: "📦",
    videos: [
      {
        id: 7,
        title: "Suppliers and Purchase Orders",
        duration: "",
        description: "Register suppliers and raise purchase orders for stock.",
        url: "/videos/Suppliers and Purchase Orders.mp4",
      },
      {
        id: 8,
        title: "Payments, Receiving and Dashboard",
        duration: "",
        description: "Track supplier payments, receive stock and use the procurement dashboard.",
        url: "/videos/payments receiving and dashboard.mp4",
      },
    ],
  },
  {
    category: "Inventory",
    icon: "🏪",
    videos: [
      {
        id: 9,
        title: "Inventory",
        duration: "",
        description: "Manage stock levels, movements and adjustments in the inventory module.",
        url: "/videos/Inventory.mp4",
      },
    ],
  },
];

// ─── Video Modal ──────────────────────────────────────────────────────
interface VideoModalProps {
  video: Video | null;
  onClose: () => void;
}

const VideoModal: React.FC<VideoModalProps> = ({ video, onClose }) => {
  if (!video) return null;
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span style={styles.modalTitle}>{video.title}</span>
          <button style={styles.closeBtn} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div style={styles.videoWrapper}>
          {video.url ? (
            <video controls width="100%" style={{ borderRadius: 8, maxHeight: 400 }}>
              <source src={video.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div style={styles.videoPlaceholder}>
              <PlayCircleOutlineIcon style={{ fontSize: 64, color: "#4caf50" }} />
              <p style={{ color: "#aaa", marginTop: 12, fontWeight: 600 }}>
                Video coming soon
              </p>
              <p style={{ color: "#666", fontSize: 13, maxWidth: 360, textAlign: "center" }}>
                {video.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────
const DocumentationPage: React.FC = () => {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", ...documentationData.map((d) => d.category)];

  const filtered = documentationData
    .map((section) => ({
      ...section,
      videos: section.videos.filter(
        (v) =>
          (activeCategory === "All" || activeCategory === section.category) &&
          (v.title.toLowerCase().includes(search.toLowerCase()) ||
            v.description.toLowerCase().includes(search.toLowerCase()))
      ),
    }))
    .filter((section) => section.videos.length > 0);

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.heading}>📖 User Documentation</h1>
        <p style={styles.subheading}>
          Step-by-step video guides to help you get the most out of VegPOS
        </p>

        {/* Search */}
        <div style={styles.searchBox}>
          <SearchIcon style={{ color: "#888", marginRight: 8 }} />
          <input
            style={styles.searchInput}
            placeholder="Search videos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Category Filter */}
        <div style={styles.filterRow}>
          {categories.map((cat) => (
            <button
              key={cat}
              style={{
                ...styles.filterBtn,
                ...(activeCategory === cat ? styles.filterBtnActive : {}),
              }}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Video Sections */}
      <div style={styles.content}>
        {filtered.length === 0 ? (
          <p style={{ color: "#888", textAlign: "center", marginTop: 40 }}>
            No videos found.
          </p>
        ) : (
          filtered.map((section) => (
            <div key={section.category} style={styles.section}>
              <div style={styles.sectionHeader}>
                <span style={styles.sectionIcon}>{section.icon}</span>
                <h2 style={styles.sectionTitle}>{section.category}</h2>
                <span style={styles.videoCount}>
                  {section.videos.length} video
                  {section.videos.length > 1 ? "s" : ""}
                </span>
              </div>

              <div style={styles.grid}>
                {section.videos.map((video) => (
                  <div
                    key={video.id}
                    style={styles.card}
                    onClick={() => setSelectedVideo(video)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(76,175,80,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                    }}
                  >
                    <div style={styles.thumbnail}>
                      <PlayCircleOutlineIcon style={{ fontSize: 44, color: "#4caf50" }} />
                      {video.duration && (
                        <span style={styles.duration}>{video.duration}</span>
                      )}
                    </div>
                    <div style={styles.cardBody}>
                      <h3 style={styles.cardTitle}>{video.title}</h3>
                      <p style={styles.cardDesc}>{video.description}</p>
                    </div>
                    <div style={styles.cardFooter}>
                      <span style={styles.watchBtn}>▶ Watch Now</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────
const styles: Record<string, CSSProperties> = {
  page: {
    backgroundColor: "#f5f6fa",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', sans-serif",
  },
  header: {
    background: "linear-gradient(135deg, #1b5e20 0%, #388e3c 100%)",
    padding: "36px 32px 28px",
    color: "#fff",
  },
  heading: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: "-0.5px",
  },
  subheading: {
    margin: "8px 0 20px",
    fontSize: 14,
    opacity: 0.85,
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    background: "#fff",
    borderRadius: 8,
    padding: "8px 14px",
    maxWidth: 420,
    marginBottom: 16,
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: 14,
    width: "100%",
    color: "#333",
  },
  filterRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  filterBtn: {
    padding: "6px 14px",
    borderRadius: 20,
    border: "1px solid rgba(255,255,255,0.4)",
    background: "transparent",
    color: "#fff",
    cursor: "pointer",
    fontSize: 13,
    transition: "all 0.2s",
  },
  filterBtnActive: {
    background: "#fff",
    color: "#2e7d32",
    fontWeight: 600,
    border: "1px solid #fff",
  },
  content: {
    padding: "28px 32px",
    maxWidth: 1200,
  },
  section: {
    marginBottom: 40,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },
  sectionIcon: {
    fontSize: 22,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: "#1b5e20",
  },
  videoCount: {
    fontSize: 12,
    background: "#e8f5e9",
    color: "#2e7d32",
    padding: "2px 10px",
    borderRadius: 12,
    fontWeight: 600,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 16,
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  thumbnail: {
    background: "#e8f5e9",
    height: 110,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  duration: {
    position: "absolute",
    bottom: 8,
    right: 10,
    fontSize: 11,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    padding: "2px 7px",
    borderRadius: 4,
  },
  cardBody: {
    padding: "12px 14px 8px",
    flex: 1,
  },
  cardTitle: {
    margin: "0 0 6px",
    fontSize: 14,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  cardDesc: {
    margin: 0,
    fontSize: 12,
    color: "#666",
    lineHeight: 1.5,
  },
  cardFooter: {
    padding: "10px 14px",
    borderTop: "1px solid #f0f0f0",
  },
  watchBtn: {
    fontSize: 12,
    color: "#2e7d32",
    fontWeight: 600,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "90%",
    maxWidth: 720,
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 20px",
    borderBottom: "1px solid #eee",
  },
  modalTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: "#1a1a1a",
  },
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#666",
    display: "flex",
  },
  videoWrapper: {
    padding: 20,
  },
  videoPlaceholder: {
    background: "#f5f5f5",
    borderRadius: 10,
    height: 320,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};

export default DocumentationPage;